import axios from "axios";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { getProductImageUrl, extractProductImagePath, applyStoreTitle } from "../../../util/storeLogoHelper";
import SEOHead from "../../common/SEOHead";
import { PageColors, pageWords, COLLECTIONS_COLORS, COLLECTIONS_SCOPE, COLLECTIONS_TEXTS } from "../../../themes/pageCustomization";
import { ActiveThemeContext } from "../../../themes/themeCache";
import { collectionsPageSettings, collectionPageStyle } from "../../../themes/collectionPageSettings";

export default function ViewCategory() {
  const [pages, setPages] = useState({});
  const [meta, setMeta] = useState(null);
  const [failed, setFailed] = useState(false);
  const [page, setPage] = useState(1);
  const theme = useContext(ActiveThemeContext);
  const look = collectionsPageSettings(theme?.theme?.settings || {});
  const words = pageWords(COLLECTIONS_TEXTS, theme?.theme?.settings || {});
  const pageSize = look.perPage;
  const loadMore = look.pagination === "more";
  const pagesRef = useRef({});
  const inflight = useRef(new Map());

  useEffect(() => {
    window.scrollTo(0, 0);
    applyStoreTitle(null, "Collections");
  }, []);

  // Loads one page of collections. Pages are cached, and a page already on
  // its way is not requested twice.
  const loadPage = useCallback((number) => {
    const key = `${pageSize}:${number}`;
    if (pagesRef.current[key]) return Promise.resolve(pagesRef.current[key]);
    if (inflight.current.has(key)) return inflight.current.get(key);
    const request = axios.get(`/api/getcategory?page=${number}&per_page=${pageSize}`).then((res) => {
      if (res.data.status !== 200) return null;
      const entry = { items: res.data.category || [], meta: res.data.meta || { total: 0, page_count: 1 } };
      pagesRef.current[key] = entry;
      setPages((current) => ({ ...current, [key]: entry }));
      return entry;
    }).finally(() => {
      inflight.current.delete(key);
    });
    inflight.current.set(key, request);
    return request;
  }, [pageSize]);

  useEffect(() => {
    let active = true;
    loadPage(page).then((entry) => {
      if (!active || !entry) return;
      setMeta(entry.meta);
      // Prefetch the next page so moving forward is instant.
      if (page < entry.meta.page_count) loadPage(page + 1).catch(() => {});
    }).catch(() => {
      if (active) setFailed(true);
    });
    return () => {
      active = false;
    };
  }, [page, loadPage]);

  const loading = meta === null && !failed;
  const total = meta?.total || 0;
  const pageCount = Math.max(1, meta?.page_count || 1);
  const safePage = Math.min(page, pageCount);
  const currentEntry = pages[`${pageSize}:${page}`];
  const waiting = !currentEntry;

  const categoryList = useMemo(() => {
    if (!loadMore) return currentEntry ? currentEntry.items : [];
    const list = [];
    for (let number = 1; number <= page; number += 1) {
      const entry = pages[`${pageSize}:${number}`];
      if (!entry) break;
      list.push(...entry.items);
    }
    return list;
  }, [loadMore, currentEntry, pages, pageSize, page]);

  const goToPage = (number) => {
    setPage(number);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const skeleton = (
    <div className="shop-index-grid" aria-busy="true">
      {Array.from({ length: Math.min(pageSize, 6) }, (_, n) => (
        <div className="shop-index-card is-loading" key={n} />
      ))}
    </div>
  );

  return (
    <div className="shop-collection" style={collectionPageStyle(look)} data-theme-group="collections-page" data-theme-label="Collections page">
      <PageColors scope={COLLECTIONS_SCOPE} colors={COLLECTIONS_COLORS} settings={theme?.theme?.settings || {}} />
      <SEOHead
        title="Collections"
        description="Browse our complete catalog of curated collections and departments with nationwide delivery."
        url="/collection"
        type="website"
      />

      <div className="container shop-collection-wrap">
        {look.showBreadcrumbs && (
          <ul className="shop-crumbs">
            <li><Link to="/">Home</Link></li>
            <li aria-hidden="true">/</li>
            <li className="is-current">Collections</li>
          </ul>
        )}

        <header className="shop-collection-head">
          <h1>{words.title}</h1>
          {!loading && (
            <p>{total} {total === 1 ? words.collection : words.collections}</p>
          )}
        </header>

        {loading ? skeleton : total === 0 ? (
          <div className="shop-empty">
            <h2>{words.emptyTitle}</h2>
            <p>{words.emptyText}</p>
            <Link to="/" className="shop-clear">Return home</Link>
          </div>
        ) : categoryList.length === 0 && waiting ? skeleton : (
          <div className="shop-index-grid" style={waiting && !loadMore ? { opacity: 0.55, transition: "opacity .15s" } : undefined} aria-busy={waiting}>
            {categoryList.map((item) => {
              const imagePath = extractProductImagePath(item.image);
              const count = Number(item.products_count || 0);
              return (
                <Link to={`/collection/${item.slug}`} className="shop-index-card" key={item.id}>
                  {imagePath ? (
                    <img src={getProductImageUrl(imagePath)} alt="" loading="lazy" decoding="async" />
                  ) : (
                    <span className="shop-index-fallback" aria-hidden="true">{item.name?.charAt(0) || "C"}</span>
                  )}
                  <span
                    className="shop-index-caption"
                    style={{ background: `linear-gradient(transparent, ${look.indexOverlay})` }}
                  >
                    <strong>{item.name}</strong>
                    {look.indexShowCount && <small>{count} {count === 1 ? words.product : words.products}</small>}
                  </span>
                </Link>
              );
            })}
          </div>
        )}

        {!loading && loadMore && categoryList.length > 0 && page < pageCount && (
          <button type="button" className="shop-load" disabled={waiting} onClick={() => setPage((number) => number + 1)}>
            {waiting ? "Loading…" : words.loadMore}
          </button>
        )}
        {!loading && !loadMore && pageCount > 1 && (
          <nav className="shop-pages" aria-label="Collection list pages">
            <button type="button" disabled={safePage === 1} onClick={() => goToPage(safePage - 1)} aria-label="Previous page">
              <FaChevronLeft size={11} />
            </button>
            {Array.from({ length: pageCount }, (_, index) => index + 1)
              .filter((number) => number === 1 || number === pageCount || Math.abs(number - safePage) <= 1)
              .map((number, index, list) => (
                <React.Fragment key={number}>
                  {index > 0 && list[index - 1] !== number - 1 && <span>…</span>}
                  <button
                    type="button"
                    className={number === safePage ? "is-current" : ""}
                    onClick={() => goToPage(number)}
                    aria-current={number === safePage ? "page" : undefined}
                  >
                    {number}
                  </button>
                </React.Fragment>
              ))}
            <button type="button" disabled={safePage === pageCount} onClick={() => goToPage(safePage + 1)} aria-label="Next page">
              <FaChevronRight size={11} />
            </button>
          </nav>
        )}
      </div>
    </div>
  );
}
